import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Route, Save, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { PageHeader, Tabs } from "@/features/power_projects/components/ui";
import { downloadBlob } from "@/utils/download";
import * as api from "../api";
import { downloadCardsPdfZip } from "../cardsZip";
import {
  useCreateVariant,
  useDictionaries,
  useDrawing,
  useDuplicateVariant,
  useFetchAddresses,
  usePatchVariant,
  useRecompute,
  useResults,
  useSaveDrawing,
  useTechCalcProject,
} from "../hooks";
import type { RouteImportResult, TechCalcDrawing } from "../types";
import {
  EMPTY_DRAWING,
  extractApiError,
  suggestPoleFunctions,
  type FunctionSuggestion,
} from "../utils";
import { TechCalcMap, type TechCalcMapHandle } from "../components/TechCalcMap";
import { FunctionSuggestionsModal } from "../components/FunctionSuggestionsModal";
import { TechCalcResultsPanel } from "../components/TechCalcResultsPanel";
import { RouteImportDialog } from "../components/RouteImportDialog";
import { TechCalcVariantSwitcher } from "../components/TechCalcVariantSwitcher";

type WorkspaceTab = "drawing" | "results";

/**
 * Workspace jednego projektu obliczeń: wybór wariantu + zakładki Rysunek/Wyniki.
 * Mapa jest zamontowana raz na wariant i wspólna dla obu zakładek — w „Wynikach"
 * przechodzi w tryb tylko-do-odczytu i koloruje słupy statusem obliczeń.
 */
export function TechCalcWorkspacePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, isLoading } = useTechCalcProject(projectId);
  const variants = project?.variants ?? [];

  const [activeVariantId, setActiveVariantId] = useState<string | null>(null);
  useEffect(() => {
    if (!variants.length) {
      setActiveVariantId(null);
      return;
    }
    if (!activeVariantId || !variants.some((v) => v.id === activeVariantId)) {
      setActiveVariantId(variants[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants.map((v) => v.id).join(",")]);

  const activeVariant = variants.find((v) => v.id === activeVariantId) ?? null;

  const [tab, setTab] = useState<WorkspaceTab>("drawing");
  const [dirty, setDirty] = useState(false);
  const fcRef = useRef<TechCalcDrawing>(EMPTY_DRAWING);
  const mapHandleRef = useRef<TechCalcMapHandle>(null);
  // Bumped to remount the map with freshly refetched drawing (fetch-addresses).
  const [mapEpoch, setMapEpoch] = useState(0);
  const [suggestions, setSuggestions] = useState<FunctionSuggestion[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [routeImportOpen, setRouteImportOpen] = useState(false);

  // Wczytanie trasy sięga po dane projektów ZE — opcja tylko dla osób z dostępem
  // do DLC_01 (backend odpowiada im 403, więc przycisk nie miałby czego otworzyć).
  const user = useAuthStore((s) => s.user);
  const canImportRoute =
    !!user && user.role !== "contractor" && !!user.accessible_dlc_modules?.includes("dlc_01");

  const { data: dictionaries } = useDictionaries();
  const drawingQ = useDrawing(activeVariantId ?? undefined);
  const resultsQ = useResults(activeVariantId ?? undefined);
  const save = useSaveDrawing(activeVariantId ?? "");
  const recompute = useRecompute(activeVariantId ?? "");
  const createVariant = useCreateVariant(projectId ?? "");
  const duplicateVariant = useDuplicateVariant(projectId ?? "");
  const patchVariant = usePatchVariant(projectId ?? "");
  const fetchAddresses = useFetchAddresses(activeVariantId ?? "");

  // Auto-clear the notice toast (fetch-addresses summary).
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 8000);
    return () => clearTimeout(t);
  }, [notice]);

  // Wsp. przyłączy — edited locally, committed on blur.
  const [coeff, setCoeff] = useState("");
  useEffect(() => {
    setCoeff(activeVariant ? String(activeVariant.access_lines_coeff) : "");
  }, [activeVariantId, activeVariant?.access_lines_coeff]); // eslint-disable-line react-hooks/exhaustive-deps

  const [exporting, setExporting] = useState(false);

  const handleSave = () => {
    if (!activeVariantId) return;
    save.mutate(fcRef.current, {
      onSuccess: () => setDirty(false),
      onError: (err) => window.alert(extractApiError(err)),
    });
  };

  const handleRecompute = () => {
    if (!activeVariantId) return;
    if (
      dirty &&
      !window.confirm(
        "Masz niezapisane zmiany rysunku — obliczenia policzą się na OSTATNIO ZAPISANYM rysunku. Kontynuować?",
      )
    )
      return;
    recompute.mutate(undefined, {
      onError: (err) => window.alert(extractApiError(err)),
    });
  };

  const handleExport = async () => {
    if (!activeVariantId) return;
    setExporting(true);
    try {
      const blob = await api.downloadVariantXlsx(activeVariantId);
      const safe = (activeVariant?.name ?? "wariant").replace(/[^\p{L}\p{N}_-]+/gu, "_");
      downloadBlob(blob, `obliczenia_${safe}.xlsx`);
    } catch (err) {
      window.alert(extractApiError(err));
    } finally {
      setExporting(false);
    }
  };

  // ZIP kart obliczeń w PDF — karty renderują się na froncie z ZAPISANEGO
  // rysunku i ostatnich wyników (jak strona karty), a backend drukuje je do
  // PDF w Chromium i odsyła gotowe archiwum.
  const [cardsExporting, setCardsExporting] = useState(false);
  const handleDownloadCards = async () => {
    if (!project || !activeVariant || !drawingQ.data || !resultsQ.data) return;
    setCardsExporting(true);
    try {
      const { blob, count } = await downloadCardsPdfZip({
        variantId: activeVariant.id,
        projectName: project.name,
        variantName: activeVariant.name,
        drawing: drawingQ.data,
        results: resultsQ.data,
      });
      const safe = activeVariant.name.replace(/[^\p{L}\p{N}_-]+/gu, "_");
      downloadBlob(blob, `karty_${safe}.zip`);
      setNotice(`Spakowano ${count} kart obliczeń (PDF) do ZIP.`);
    } catch (err) {
      window.alert(extractApiError(err));
    } finally {
      setCardsExporting(false);
    }
  };

  const handleRouteImported = ({ variant, summary }: RouteImportResult) => {
    setRouteImportOpen(false);
    setActiveVariantId(variant.id);
    setDirty(false);
    setTab("drawing");
    setNotice(
      `Wczytano trasę ${summary.source.project_name} (${summary.source.route_variant}` +
        `${summary.source.route_version}): słupy ${summary.poles}, przyłącza ` +
        `${summary.access_points}, przęsła ${summary.spans}. ` +
        summary.warnings.join(" "),
    );
  };

  const handleSuggestFunctions = () => {
    const list = suggestPoleFunctions(fcRef.current);
    if (!list.length) {
      window.alert("Rysunek nie zawiera słupów — nie ma czego sugerować.");
      return;
    }
    setSuggestions(list);
  };

  const handleFetchAddresses = () => {
    if (!activeVariantId) return;
    if (dirty) {
      window.alert(
        "Zapisz rysunek przed pobraniem adresów — adresy są dopisywane do zapisanych słupów.",
      );
      return;
    }
    fetchAddresses.mutate(undefined, {
      onSuccess: async (r) => {
        const errCount = r.errors?.length ?? 0;
        setNotice(
          `Adresy OSM: zaktualizowano ${r.updated}, pominięto ${r.skipped}` +
            (errCount ? `, błędy: ${errCount} (${r.errors[0]})` : ""),
        );
        // Reload the drawing INTO the map: refetch first, then remount via epoch
        // (the mounted map deliberately ignores background drawing refetches).
        await drawingQ.refetch();
        setMapEpoch((e) => e + 1);
      },
      onError: (err) => window.alert(extractApiError(err)),
    });
  };

  const commitCoeff = () => {
    if (!activeVariant) return;
    const n = Number(coeff.replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      setCoeff(String(activeVariant.access_lines_coeff));
      return;
    }
    if (n !== Number(activeVariant.access_lines_coeff)) {
      patchVariant.mutate({ id: activeVariant.id, payload: { access_lines_coeff: n } });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (!project) {
    return (
      <p className="py-16 text-center font-mono text-xs text-[var(--color-text-muted)]">
        Nie znaleziono projektu.
      </p>
    );
  }

  return (
    <div>
      <PageHeader
        backTo="/tech-calc"
        backLabel="Obliczenia techniczne"
        title={project.name}
        subtitle={project.description || undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {dirty && (
              <span className="border border-amber-600 px-2 py-1 font-mono text-[10px] font-bold uppercase text-amber-600">
                Niezapisane zmiany
              </span>
            )}
            {canImportRoute && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRouteImportOpen(true)}
                title="Utwórz nowy wariant obliczeń z trasy projektu ZE (DLC_01)"
              >
                <Route size={14} className="mr-1" /> Wczytaj trasę z ZE
              </Button>
            )}
            {tab === "drawing" && activeVariantId && (
              <>
                <Button size="sm" variant="ghost" onClick={handleSuggestFunctions}>
                  <Wand2 size={14} className="mr-1" /> Zasugeruj funkcje
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={fetchAddresses.isPending}
                  onClick={handleFetchAddresses}
                  title="Odpytaj OSM o adresy pod narysowanymi punktami (działa na zapisanym rysunku)"
                >
                  <MapPin size={14} className="mr-1" /> Pobierz adresy (OSM)
                </Button>
              </>
            )}
            <Button size="sm" disabled={!dirty || !activeVariantId} loading={save.isPending} onClick={handleSave}>
              <Save size={14} className="mr-1" /> Zapisz rysunek
            </Button>
          </div>
        }
      />

      {notice && (
        <p className="mb-3 border border-emerald-600 px-3 py-2 font-mono text-[11px] text-emerald-600">
          {notice}
        </p>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <TechCalcVariantSwitcher
          variants={variants}
          activeVariantId={activeVariantId}
          dirty={dirty}
          onSelect={(id) => {
            setActiveVariantId(id);
            setDirty(false);
          }}
          onCreate={(name) =>
            createVariant.mutate(name, { onSuccess: (v) => setActiveVariantId(v.id) })
          }
          onRename={(id, name) => patchVariant.mutate({ id, payload: { name } })}
          onDuplicate={(id, name) =>
            duplicateVariant.mutate(
              { id, name },
              {
                onSuccess: (v) => {
                  // Switch to the copy — its drawing is the saved original, so
                  // any unsaved local edits are deliberately dropped (the user
                  // confirmed that in the switcher when dirty).
                  setActiveVariantId(v.id);
                  setDirty(false);
                },
                onError: (err) => window.alert(extractApiError(err)),
              },
            )
          }
          createPending={createVariant.isPending}
          duplicatePending={duplicateVariant.isPending}
        />
        {activeVariant && (
          <label className="flex items-center gap-1.5 font-mono text-[11px] text-[var(--color-text-secondary)]">
            Wsp. przyłączy
            <input
              type="number"
              step="any"
              min={0}
              className="input-base w-20 py-1 text-right font-mono text-[12px]"
              value={coeff}
              onChange={(e) => setCoeff(e.target.value)}
              onBlur={commitCoeff}
              onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
              title="Współczynnik obciążenia przyłączy abonenckich dla tego wariantu"
            />
          </label>
        )}
      </div>

      {!activeVariantId ? (
        <div className="card p-8 text-center">
          <p className="mb-3 font-mono text-xs text-[var(--color-text-muted)]">
            Ten projekt nie ma jeszcze żadnego wariantu obliczeń.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              size="sm"
              loading={createVariant.isPending}
              onClick={() =>
                createVariant.mutate("Wariant A", { onSuccess: (v) => setActiveVariantId(v.id) })
              }
            >
              Utwórz pierwszy wariant
            </Button>
            {canImportRoute && (
              <Button size="sm" variant="ghost" onClick={() => setRouteImportOpen(true)}>
                <Route size={14} className="mr-1" /> Wczytaj trasę z projektu ZE
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          <Tabs<WorkspaceTab>
            tabs={[
              { key: "drawing", label: "Rysunek" },
              { key: "results", label: "Wyniki" },
            ]}
            active={tab}
            onChange={setTab}
          />

          <div className="flex h-[calc(100vh-320px)] min-h-[480px] overflow-hidden border border-[var(--color-border)]">
            {drawingQ.isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <TechCalcMap
                key={`${activeVariantId}:${mapEpoch}`}
                ref={mapHandleRef}
                className="min-w-0 flex-1"
                initialDrawing={drawingQ.data ?? EMPTY_DRAWING}
                dictionaries={dictionaries}
                onChange={(fc, isDirty) => {
                  fcRef.current = fc;
                  if (isDirty) setDirty(true);
                }}
                results={resultsQ.data?.results?.poles ?? null}
                showResults={tab === "results"}
              />
            )}
            {tab === "results" && (
              <div className="w-[440px] shrink-0 border-l border-[var(--color-border)] bg-[var(--color-bg-surface)]">
                <TechCalcResultsPanel
                  projectId={project.id}
                  variantId={activeVariantId}
                  drawing={fcRef.current}
                  data={resultsQ.data}
                  loading={resultsQ.isLoading}
                  onRecompute={handleRecompute}
                  recomputePending={recompute.isPending}
                  onExport={handleExport}
                  exportPending={exporting}
                  onDownloadCards={handleDownloadCards}
                  downloadCardsPending={cardsExporting}
                  dirty={dirty}
                />
              </div>
            )}
          </div>
        </>
      )}

      {canImportRoute && (
        <RouteImportDialog
          open={routeImportOpen}
          projectId={project.id}
          onClose={() => setRouteImportOpen(false)}
          onImported={handleRouteImported}
        />
      )}

      {suggestions && (
        <FunctionSuggestionsModal
          suggestions={suggestions}
          onApply={(updates) => {
            mapHandleRef.current?.applyPoleParams(
              Object.fromEntries(
                Object.entries(updates).map(([ref, fn]) => [ref, { function: fn }]),
              ),
            );
            setSuggestions(null);
          }}
          onClose={() => setSuggestions(null)}
        />
      )}
    </div>
  );
}
